import { getConsent, sharedDomainAttribute } from "@/consent/consent";
import {
  fbclidOf,
  META_CLICK_ID_COOKIE,
  META_COOKIE_MAX_AGE_SECONDS,
  metaClickId,
  type MetaAddToCartData,
  type MetaAddToCartRequest,
} from "@/lib/metaConversions";

const readCookie = (name: string): string | undefined => {
  const prefix = `${name}=`;
  const entry = document.cookie
    .split("; ")
    .find((cookie) => cookie.startsWith(prefix));
  return entry === undefined
    ? undefined
    : decodeURIComponent(entry.slice(prefix.length));
};

/**
 * Stores the ad click a visitor landed from as `_fbc`, the same value the
 * pixel would write, so the click reaches the Conversions API even when the
 * pixel is blocked. Call it only with marketing consent.
 */
export const rememberMetaClick = (): void => {
  try {
    const fbclid = new URLSearchParams(window.location.search).get("fbclid");
    if (!fbclid) return;
    const existing = readCookie(META_CLICK_ID_COOKIE);
    if (existing !== undefined && fbclidOf(existing) === fbclid) return;
    document.cookie = `${META_CLICK_ID_COOKIE}=${metaClickId(fbclid, Date.now())}; Max-Age=${META_COOKIE_MAX_AGE_SECONDS}; Path=/; SameSite=Lax; Secure${sharedDomainAttribute()}`;
  } catch (error) {
    console.error("Failed to store the Meta click ID:", error);
  }
};

/**
 * AddToCart to Meta from our server, which an ad blocker can't stop. The
 * pixel sends the same event under the same ID when it's loaded, and Meta
 * keeps one of the two. `buildData` runs inside the try, so a payload that
 * can't be built never reaches the caller (a cart add).
 */
export const trackMetaAddToCart = (
  buildData: () => MetaAddToCartData | null,
): void => {
  if (typeof window === "undefined" || !getConsent().marketing) return;
  try {
    const customData = buildData();
    if (customData === null) return;
    const eventId = crypto.randomUUID();
    window.fbq?.("track", "AddToCart", customData, { eventID: eventId });
    const body: MetaAddToCartRequest = {
      eventId,
      eventSourceUrl: window.location.href,
      customData,
    };
    fetch("/api/meta/add-to-cart", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      keepalive: true,
    })
      .then((response) => {
        if (!response.ok) {
          console.error(
            `Failed to send Meta AddToCart: route returned ${response.status}`,
          );
        }
      })
      .catch((error) => console.error("Failed to send Meta AddToCart:", error));
  } catch (error) {
    console.error("Failed to send Meta AddToCart:", error);
  }
};
